import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { checkIsAdminSync, checkStaffAccess } from "../helpers/helpers";
import logger from "../utils/logger";

/**
 * useUserRoleFlags — role checks shared by the Sidebar and the homepage
 * top nav ("More" menu). Same logic as the Sidebar's useEffects, lifted
 * into one hook so the two surfaces never drift apart.
 *
 * Returns: { isAdmin, isStaff, isPartner, isPromoter, isUGC, hasSharedForms }
 */
export default function useUserRoleFlags(userId) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [isPromoter, setIsPromoter] = useState(false);
  const [isUGC, setIsUGC] = useState(false);
  const [hasSharedForms, setHasSharedForms] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!userId) { setIsAdmin(false); return; }
      try { setIsAdmin(await checkIsAdminSync()); } catch (error) { logger.error("Error checking admin status:", error); setIsAdmin(false); }
    };
    checkAdminStatus();
  }, [userId]);

  useEffect(() => {
    const checkStaff = async () => {
      if (!userId) { setIsStaff(false); return; }
      try {
        const access = await checkStaffAccess();
        setIsStaff(access.hasAccess && !access.isFullAdmin);
      } catch { setIsStaff(false); }
    };
    checkStaff();
  }, [userId]);

  useEffect(() => {
    const checkPartner = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const authId = auth?.user?.id;
        if (!authId) { setIsPartner(false); return; }
        const { data, error } = await supabase.from("User").select("id, partner_id").eq("auth_user_id", authId).maybeSingle();
        if (error) throw error;
        setIsPartner(!!data?.partner_id);
      } catch { setIsPartner(false); }
    };
    checkPartner();
  }, [userId]);

  useEffect(() => {
    const checkPromoter = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setIsPromoter(false); return; }
        const { data } = await supabase.from("promoters").select("id").eq("user_id", auth.user.id).eq("status", "active").maybeSingle();
        setIsPromoter(!!data);
      } catch { setIsPromoter(false); }
    };
    checkPromoter();
  }, [userId]);

  useEffect(() => {
    const checkUGC = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setIsUGC(false); return; }
        const authId = auth.user.id;

        const { data: dbUser } = await supabase.from("User")
          .select("id").eq("auth_user_id", authId).maybeSingle();
        const numericUserId = dbUser?.id;

        let data = null;
        if (numericUserId != null) {
          ({ data } = await supabase.from("ambassadors")
            .select("id").eq("user_id", numericUserId)
            .eq("is_ugc_partner", true).maybeSingle());
        }

        if (!data && auth.user.email) {
          const { data: emailMatch } = await supabase.from("ambassadors")
            .select("id").ilike("ugc_email", auth.user.email.trim())
            .eq("is_ugc_partner", true).maybeSingle();
          setIsUGC(!!emailMatch);
          return;
        }

        setIsUGC(!!data);
      } catch { setIsUGC(false); }
    };
    checkUGC();
  }, [userId]);

  useEffect(() => {
    const checkSharedForms = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user?.id) { setHasSharedForms(false); return; }
        const { data: dbUser } = await supabase.from("User").select("id").eq("auth_user_id", auth.user.id).maybeSingle();
        if (!dbUser?.id) { setHasSharedForms(false); return; }
        const { data } = await supabase.from("FormShares").select("id").eq("user_id", dbUser.id).limit(1).maybeSingle();
        setHasSharedForms(!!data);
      } catch { setHasSharedForms(false); }
    };
    checkSharedForms();
  }, [userId]);

  return { isAdmin, isStaff, isPartner, isPromoter, isUGC, hasSharedForms };
}
